import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    X,
    Loader2,
    GitCompareArrows,
    AlertOctagon,
    CheckCircle2,
} from "lucide-react";
import { getSopDiff } from "../lib/api";

/**
 * Side-by-side rule-drift comparison between two revisions of an SOP.
 * Auto-picks the latest two distinct content hashes (the most demo-relevant
 * pair). Displays a coloured unified diff plus the list of "orphan" systems
 * still on the older revision.
 */
export default function SopDiffModal({ filename, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        getSopDiff(filename)
            .then((d) => {
                if (!cancelled) setData(d);
            })
            .catch((e) => {
                if (!cancelled)
                    setError(
                        e?.response?.data?.detail ||
                            "Couldn't load revision diff.",
                    );
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [filename]);

    const renderDiffLine = (line, idx) => {
        let cls =
            "block whitespace-pre-wrap px-3 py-[1px] font-mono text-[12px] leading-[18px]";
        if (line.startsWith("+++") || line.startsWith("---")) {
            cls += " text-neutral-400";
        } else if (line.startsWith("@@")) {
            cls += " text-[#0e7490] bg-[#ECFEFF]";
        } else if (line.startsWith("+")) {
            cls += " text-[#166534] bg-[#DCFCE7]";
        } else if (line.startsWith("-")) {
            cls += " text-[#991B1B] bg-[#FEE2E2]";
        } else {
            cls += " text-neutral-600";
        }
        return (
            <span key={idx} className={cls}>
                {line || " "}
            </span>
        );
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
            data-testid="sop-diff-modal"
            onClick={onClose}
        >
            <div
                className="bg-white w-full max-w-5xl max-h-[90vh] border border-hair flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-5 py-3 border-b border-hair">
                    <div className="min-w-0 flex items-center gap-3">
                        <GitCompareArrows
                            size={16}
                            className="text-[#C2410C] shrink-0"
                        />
                        <div className="min-w-0">
                            <div className="overline">Compare revisions</div>
                            <div className="heading font-medium tracking-tight text-sm mt-1 truncate">
                                {filename}
                            </div>
                        </div>
                    </div>
                    <button
                        data-testid="btn-close-diff"
                        onClick={onClose}
                        className="w-8 h-8 inline-flex items-center justify-center border border-hair rounded-sm hover:bg-neutral-50"
                    >
                        <X size={14} />
                    </button>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto">
                    {loading && (
                        <div className="h-full flex items-center justify-center text-neutral-500 text-sm gap-2 py-24">
                            <Loader2 className="animate-spin" size={16} /> Loading
                            diff…
                        </div>
                    )}
                    {!loading && error && (
                        <div className="text-center py-24 text-neutral-600">
                            <AlertOctagon
                                className="mx-auto text-neutral-300"
                                size={32}
                            />
                            <p className="text-sm mt-4">{error}</p>
                        </div>
                    )}

                    {!loading && data && (
                        <>
                            {/* Header strip with rev_a / rev_b summary */}
                            <div className="grid md:grid-cols-2 border-b border-hair bg-[#fafafa]">
                                {[
                                    [data.rev_a, "Older revision"],
                                    [data.rev_b, "Newer revision"],
                                ].map(([rev, label], i) => (
                                    <div
                                        key={label}
                                        className={`p-5 ${
                                            i === 0
                                                ? "border-r border-hair"
                                                : ""
                                        }`}
                                    >
                                        <div className="overline mb-2">
                                            {label}
                                        </div>
                                        <div className="mono text-xs text-neutral-500 truncate">
                                            sha:{" "}
                                            {(rev.sha256 || "").slice(0, 16)}…
                                        </div>
                                        <div className="text-sm mt-2">
                                            <span className="num">
                                                {rev.system_count}
                                            </span>{" "}
                                            system
                                            {rev.system_count === 1
                                                ? ""
                                                : "s"}{" "}
                                            on this revision
                                        </div>
                                        <div className="mono text-[11px] text-neutral-500 mt-2">
                                            {rev.uploaded_at
                                                ? new Date(
                                                      rev.uploaded_at,
                                                  ).toLocaleString()
                                                : "—"}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Stats strip */}
                            <div className="grid grid-cols-3 border-b border-hair">
                                <div className="p-4 text-center border-r border-hair">
                                    <div className="overline">Added</div>
                                    <div className="heading text-2xl text-[#166534] font-medium tracking-tighter mt-1">
                                        +{data.added_lines}
                                    </div>
                                </div>
                                <div className="p-4 text-center border-r border-hair">
                                    <div className="overline">Removed</div>
                                    <div className="heading text-2xl text-[#991B1B] font-medium tracking-tighter mt-1">
                                        −{data.removed_lines}
                                    </div>
                                </div>
                                <div className="p-4 text-center">
                                    <div className="overline">Similarity</div>
                                    <div className="heading text-2xl font-medium tracking-tighter mt-1">
                                        {data.similarity_percent}%
                                    </div>
                                </div>
                            </div>

                            {/* Orphan systems banner — the regulator-pleasing bit */}
                            {data.orphan_systems &&
                                data.orphan_systems.length > 0 && (
                                    <div
                                        data-testid="orphan-systems-banner"
                                        className="px-5 py-4 border-b border-hair bg-[#FEF2F2]"
                                    >
                                        <div className="flex items-start gap-3">
                                            <AlertOctagon
                                                size={16}
                                                className="text-[#991B1B] mt-[2px] shrink-0"
                                            />
                                            <div className="text-[13px] text-neutral-800 leading-relaxed">
                                                <strong className="heading tracking-tight">
                                                    {data.orphan_systems.length}{" "}
                                                    system
                                                    {data.orphan_systems
                                                        .length === 1
                                                        ? ""
                                                        : "s"}{" "}
                                                    still on the older revision.
                                                </strong>{" "}
                                                Each is reading rules from a
                                                version that no longer
                                                represents the controlled SOP —
                                                update them to close the drift:
                                                <div className="flex gap-2 flex-wrap mt-3">
                                                    {data.orphan_systems.map(
                                                        (s) => (
                                                            <Link
                                                                key={s.id}
                                                                to={`/systems/${s.id}`}
                                                                className="inline-flex items-center gap-1 mono text-[11px] uppercase tracking-wider px-2 py-[3px] border border-[#FCA5A5] bg-white text-[#991B1B] rounded-sm hover:bg-[#FEE2E2]"
                                                                data-testid={`orphan-${s.name}`}
                                                            >
                                                                {s.name}
                                                            </Link>
                                                        ),
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            {data.orphan_systems &&
                                data.orphan_systems.length === 0 && (
                                    <div className="px-5 py-4 border-b border-hair bg-[#ECFDF5]">
                                        <div className="flex items-center gap-3 text-[13px] text-[#166534]">
                                            <CheckCircle2 size={16} />
                                            All systems are on the latest
                                            revision.
                                        </div>
                                    </div>
                                )}

                            {/* Unified diff */}
                            {data.diff && data.diff.length > 0 ? (
                                <div
                                    data-testid="diff-pane"
                                    className="bg-white"
                                >
                                    {data.diff.map((line, idx) =>
                                        renderDiffLine(line, idx),
                                    )}
                                </div>
                            ) : (
                                <div className="text-center py-12 text-neutral-500 text-sm">
                                    The two revisions have identical
                                    text content — no rule drift detected.
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
